import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Calculator, Phone, MapPin, Mail, ArrowUp, Shield, Cookie } from 'lucide-react';

const Footer = () => {
  const { language, setLanguage, t } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    'Processamento de Salários',
    'Relatórios Periódicos',
    'Apoio Fiscal',
    'Abertura de Empresas'
  ];

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-lg">
                CF<span className="text-teal-400">.Lda</span>
              </span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-2">
              Contabilidade e Fiscalidade, Lda
            </p>
            <p className="text-slate-500 text-xs mb-4">
              Contabilista Certificado (N.º OCC a indicar)
            </p>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {t.footer.tagline}
            </p>

            {/* Language Switcher */}
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">{t.footer.languages}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setLanguage('pt')}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                    language === 'pt'
                      ? 'bg-teal-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  Português
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                    language === 'en'
                      ? 'bg-teal-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  English
                </button>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t.footer.services}</h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection('services')}
                    className="text-slate-400 hover:text-teal-400 text-sm transition-colors"
                  >
                    {service}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">
              {language === 'pt' ? 'Links Rápidos' : 'Quick Links'}
            </h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('services')}
                  className="text-slate-400 hover:text-teal-400 text-sm transition-colors"
                >
                  {t.nav.services}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-slate-400 hover:text-teal-400 text-sm transition-colors"
                >
                  {t.nav.about}
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-slate-400 hover:text-teal-400 text-sm transition-colors"
                >
                  {t.nav.contact}
                </button>
              </li>
              <li className="pt-2 border-t border-slate-800">
                <Link
                  to="/politica-privacidade"
                  className="flex items-center gap-2 text-slate-400 hover:text-teal-400 text-sm transition-colors"
                >
                  <Shield className="w-3 h-3" />
                  {language === 'pt' ? 'Política de Privacidade' : 'Privacy Policy'}
                </Link>
              </li>
              <li>
                <Link
                  to="/politica-cookies"
                  className="flex items-center gap-2 text-slate-400 hover:text-teal-400 text-sm transition-colors"
                >
                  <Cookie className="w-3 h-3" />
                  {language === 'pt' ? 'Política de Cookies' : 'Cookie Policy'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t.footer.contact}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                <a
                  href="tel:911000000"
                  className="text-slate-400 hover:text-teal-400 text-sm transition-colors"
                >
                  911 000 000
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:info@cflda.pt"
                  className="text-slate-400 hover:text-teal-400 text-sm transition-colors"
                >
                  info@cflda.pt
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400 text-sm">
                  Rua Prudêncio Franco da Trindade 4,<br />2655-344 Ericeira
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} {t.footer.company}. {t.footer.rights}
            </p>
            <span className="hidden sm:inline text-slate-700">|</span>
            <p className="text-slate-500 text-xs">
              {language === 'pt' ? 'Contabilista Certificado' : 'Certified Accountant'}
            </p>
          </div>
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 text-slate-400 hover:text-teal-400 text-sm transition-colors group"
          >
            <span>{language === 'pt' ? 'Voltar ao topo' : 'Back to top'}</span>
            <ArrowUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
